import type { StateCreator } from 'zustand';
import type { SensorReading, DeviceStatus, DailySummary, AlertEvent, HealthScore, OutdoorWeather, DayHourlyDetail, AmbientReading, DeviceEvent, CultivationStage } from '../types';
import { MANUAL_ON_REASONS } from '../services/strings';
import { dataSource, getSourceLabel, fetchDayHourlyDetail, saveDeviceEvent } from '../services/dataSource';

// Bellekte tutulacak maksimum sensör okuma sayısı.
// 30 saniyede bir veri gelirse bu ~4.2 saatlik history demek.
// Daha eski veri için Supabase'den lazy fetch yapılır.
const MAX_IN_MEMORY_READINGS = 500;

export interface ClimateSlice {
  readings: SensorReading[];
  current: SensorReading | null;
  devices: DeviceStatus[];
  dailySummaries: DailySummary[];
  alerts: AlertEvent[];
  healthScore: HealthScore | null;
  outdoorWeather: OutdoorWeather | null;
  unreadAlertCount: number;
  selectedDay: DayHourlyDetail | null;
  selectedDayNum: number | null;
  primarySpeciesId: string;
  suggestedStage: CultivationStage;
  lastUpdated: string | null;
  ambientReading: AmbientReading | null;
  outdoorCurrent: SensorReading | null;
  deviceEvents: DeviceEvent[];
  savedActiveDeviceIds: string[] | null;
  rpiOnline: boolean | null;

  dataSource: string;
  dataSourceLabel: string;

  setReadings: (v: SensorReading[] | ((p: SensorReading[]) => SensorReading[])) => void;
  setCurrent: (v: SensorReading | null | ((p: SensorReading | null) => SensorReading | null)) => void;
  setDevices: (v: DeviceStatus[] | ((p: DeviceStatus[]) => DeviceStatus[])) => void;
  setDailySummaries: (v: DailySummary[] | ((p: DailySummary[]) => DailySummary[])) => void;
  setAlerts: (v: AlertEvent[] | ((p: AlertEvent[]) => AlertEvent[])) => void;
  setHealthScore: (v: HealthScore | null | ((p: HealthScore | null) => HealthScore | null)) => void;
  setOutdoorWeather: (v: OutdoorWeather | null | ((p: OutdoorWeather | null) => OutdoorWeather | null)) => void;
  setUnreadAlertCount: (v: number | ((p: number) => number)) => void;
  setSelectedDay: (v: DayHourlyDetail | null | ((p: DayHourlyDetail | null) => DayHourlyDetail | null)) => void;
  setSelectedDayNum: (v: number | null | ((p: number | null) => number | null)) => void;
  setPrimarySpeciesId: (v: string | ((p: string) => string)) => void;
  setSuggestedStage: (v: CultivationStage | ((p: CultivationStage) => CultivationStage)) => void;
  setLastUpdated: (v: string | null | ((p: string | null) => string | null)) => void;
  setAmbientReading: (v: AmbientReading | null | ((p: AmbientReading | null) => AmbientReading | null)) => void;
  setOutdoorCurrent: (v: SensorReading | null | ((p: SensorReading | null) => SensorReading | null)) => void;
  setDeviceEvents: (v: DeviceEvent[] | ((p: DeviceEvent[]) => DeviceEvent[])) => void;
  setSavedActiveDeviceIds: (v: string[] | null | ((p: string[] | null) => string[] | null)) => void;
  setRpiOnline: (v: boolean | null) => void;

  toggleDevice: (deviceId: string) => void;
  updateDevice: (id: string, updates: Partial<DeviceStatus>) => void;
  markAlertsRead: () => void;
  selectDay: (date: string, dayNumber: number) => void;
  clearSelectedDay: () => void;
  allDevicesOff: () => void;
  restoreDevices: () => void;
}

export const createClimateSlice: StateCreator<ClimateSlice, [], [], ClimateSlice> = (set, get) => ({
  readings: [],
  current: null,
  devices: [],
  dailySummaries: [],
  alerts: [],
  healthScore: null,
  outdoorWeather: null,
  unreadAlertCount: 0,
  selectedDay: null,
  selectedDayNum: null,
  primarySpeciesId: 'lion-mane',
  suggestedStage: 'fruiting',
  lastUpdated: null,
  ambientReading: null,
  outdoorCurrent: null,
  deviceEvents: [],
  savedActiveDeviceIds: null,
  rpiOnline: null,
  dataSource: dataSource,
  dataSourceLabel: getSourceLabel(),

  setReadings: (v) => set(s => {
    const next = typeof v === 'function' ? v(s.readings) : v;
    // Sliding window: en eski kayıtları at, bellek büyümesini sınırla
    return { readings: next.length > MAX_IN_MEMORY_READINGS ? next.slice(-MAX_IN_MEMORY_READINGS) : next };
  }),
  setCurrent: (v) => set(s => ({ current: typeof v === 'function' ? v(s.current) : v })),
  setDevices: (v) => set(s => ({ devices: typeof v === 'function' ? v(s.devices) : v })),
  setDailySummaries: (v) => set(s => ({ dailySummaries: typeof v === 'function' ? v(s.dailySummaries) : v })),
  setAlerts: (v) => set(s => ({ alerts: typeof v === 'function' ? v(s.alerts) : v })),
  setHealthScore: (v) => set(s => ({ healthScore: typeof v === 'function' ? v(s.healthScore) : v })),
  setOutdoorWeather: (v) => set(s => ({ outdoorWeather: typeof v === 'function' ? v(s.outdoorWeather) : v })),
  setUnreadAlertCount: (v) => set(s => ({ unreadAlertCount: typeof v === 'function' ? v(s.unreadAlertCount) : v })),
  setSelectedDay: (v) => set(s => ({ selectedDay: typeof v === 'function' ? v(s.selectedDay) : v })),
  setSelectedDayNum: (v) => set(s => ({ selectedDayNum: typeof v === 'function' ? v(s.selectedDayNum) : v })),
  setPrimarySpeciesId: (v) => set(s => ({ primarySpeciesId: typeof v === 'function' ? v(s.primarySpeciesId) : v })),
  setSuggestedStage: (v) => set(s => ({ suggestedStage: typeof v === 'function' ? v(s.suggestedStage) : v })),
  setLastUpdated: (v) => set(s => ({ lastUpdated: typeof v === 'function' ? v(s.lastUpdated) : v })),
  setAmbientReading: (v) => set(s => ({ ambientReading: typeof v === 'function' ? v(s.ambientReading) : v })),
  setOutdoorCurrent: (v) => set(s => ({ outdoorCurrent: typeof v === 'function' ? v(s.outdoorCurrent) : v })),
  setDeviceEvents: (v) => set(s => ({ deviceEvents: typeof v === 'function' ? v(s.deviceEvents) : v })),
  setSavedActiveDeviceIds: (v) => set(s => ({ savedActiveDeviceIds: typeof v === 'function' ? v(s.savedActiveDeviceIds) : v })),
  setRpiOnline: (v) => set({ rpiOnline: v }),

  toggleDevice: (deviceId: string) => get().setDevices(prev => prev.map(d => {
    if (d.id !== deviceId) return d;
    const turningOn = !d.isActive;
    const now = new Date().toISOString();
    
    if (turningOn) {
      const newEvent = {
        id: `de-${crypto.randomUUID()}`,
        deviceId: d.id,
        deviceName: d.name,
        deviceType: d.type,
        reason: MANUAL_ON_REASONS[d.type] || MANUAL_ON_REASONS.default,
        startedAt: now,
        endedAt: undefined,
        durationMinutes: undefined,
      };
      get().setDeviceEvents(evs => [newEvent, ...evs]);
      saveDeviceEvent(newEvent);
    } else {
      get().setDeviceEvents(evs => evs.map(ev => {
        if (ev.deviceId === deviceId && !ev.endedAt) {
          const startMs = new Date(ev.startedAt).getTime();
          const durationMinutes = Math.round((Date.now() - startMs) / 60000);
          const closed = { ...ev, endedAt: now, durationMinutes };
          saveDeviceEvent(closed);
          return closed;
        }
        return ev;
      }));
    }
    return { ...d, isActive: turningOn, lastToggled: now };
  })),

  updateDevice: (id, updates) => get().setDevices(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d)),
  
  markAlertsRead: () => set({ unreadAlertCount: 0 }),
  
  selectDay: (date, dayNumber) => {
    fetchDayHourlyDetail(date, dayNumber)
      .then(detail => {
        if (detail) set({ selectedDay: detail, selectedDayNum: dayNumber });
      })
      .catch(err => {
        console.error('[ClimateSlice] Gün detay yüklenemedi:', err);
      });
  },
  
  clearSelectedDay: () => set({ selectedDay: null, selectedDayNum: null }),
  
  allDevicesOff: () => {
    const { devices } = get();
    const now = new Date().toISOString();
    get().setSavedActiveDeviceIds(devices.filter(d => d.isActive).map(d => d.id));
    get().setDevices(devices.map(d => ({ ...d, isActive: false })));
    get().setDeviceEvents(evs => evs.map(ev => {
      if (!ev.endedAt) {
        const durationMinutes = Math.round((Date.now() - new Date(ev.startedAt).getTime()) / 60000);
        return { ...ev, endedAt: now, durationMinutes };
      }
      return ev;
    }));
  },
  
  restoreDevices: () => {
    const { devices, savedActiveDeviceIds } = get();
    get().setDevices(devices.map(d => ({
      ...d,
      isActive: savedActiveDeviceIds ? savedActiveDeviceIds.includes(d.id) : d.isActive,
    })));
    get().setSavedActiveDeviceIds(null);
  },
});
